import classNames from "classnames";
import { memo, useEffect, useMemo, useState } from "react";
import Button from "src/components/buttons/Button";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { getCategoryLabel } from "src/helpers/helpers";
import useNotification from "src/hooks/useNotification";
import { KeyedMutator } from "swr";
import Modal from "../Modal";
import { ProductType } from "./product-types";
import useProductsTableColumns from "./useProductsTableColumns";

const ProductsModal = ({
  isOpen,
  onClose,
  useGetData,
  usePostData,
}: {
  isOpen: boolean;
  onClose: () => void;
  useGetData: ({
    length,
    page,
    search,
  }: {
    length: number;
    page: number;
    search?: string;
  }) => {
    isLoading: boolean;
    data: any;
    error: any;
    mutate: KeyedMutator<any>;
    isValidating: boolean;
  };
  usePostData?: () => {
    execute: (...args: any) => Promise<void>;
    isLoading: boolean;
    data: any;
    error: any;
    isSuccess: boolean;
  };
}) => {
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const columns = useProductsTableColumns({ handleInputQuantityChange });
  const { errorNotification, successNotification } = useNotification();
  const { isLoading, error, data, mutate, isValidating } =
    useGetData?.({ length, page, search: "" }) ?? {};

  const {
    isLoading: isCreateLoading,
    execute: createRequest,
    isSuccess: createRequestSucceeded,
    error: createRequestFailed,
  } = usePostData?.() ?? {};

  const [productsData, setProductsData] = useState(data?.data?.rows ?? []);

  const mappedData = useMemo(() => {
    return (
      data?.data?.rows?.map((item: ProductType) => ({
        ...item,
        category: getCategoryLabel(item.category),
      })) ?? []
    );
  }, [data?.data?.rows]);

  useEffect(() => setProductsData(mappedData), [mappedData]);

  useEffect(() => {
    if (createRequestSucceeded) {
      successNotification("Created Successfully");
      onClose();
    } else if (createRequestFailed) {
      errorNotification("Something went wrong");
    }
  }, [
    createRequestFailed,
    createRequestSucceeded,
    errorNotification,
    successNotification,
  ]);

  const handleSubmit = () => {
    for (let i = 0; i < selectedProducts.length; i++) {
      if (
        Number(selectedProducts[i].inputQuantity) > selectedProducts[i].stock
      ) {
        return errorNotification("Please check Input Quantity");
      }
    }

    const payload = {
      products: selectedProducts.map((product) => ({
        id: product.id,
        quantity: Number(product.inputQuantity),
        price: product.price,
      })),
    };
    createRequest?.(payload);
  };

  function handleInputQuantityChange(quantity: string, index: number) {
    setProductsData((prev: any) =>
      prev.map((item: any, key: number) =>
        index === key ? { ...item, inputQuantity: quantity } : item
      )
    );
  }

  return (
    <Modal isOpen={isOpen} closeModal={onClose} title="Products">
      <div className="flex justify-end">
        <Button
          text={isCreateLoading ? "Loading..." : "Create Procurement Request"}
          onClick={handleSubmit}
          isDisabled={isCreateLoading || selectedProducts.length < 1}
          className={classNames({
            "opacity-50 cursor-not-allowed pointer-events-none":
              isCreateLoading,
          })}
          primary
        />
      </div>
      <div className="border border-[#D5D5D5] rounded-14px">
        <ReactTable
          columns={columns}
          data={productsData}
          isLoading={isLoading}
          checkbox={productsData?.length > 0}
          onCheckboxClick={(data) => setSelectedProducts(data)}
        />
      </div>
    </Modal>
  );
};

export default memo(ProductsModal);
